import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Envelope as Mail } from '@phosphor-icons/react';
import { useContactInfo, DEFAULT_COMPANY_DESCRIPTION } from '../hooks/useContactInfo';
import { useTranslation } from '../i18n/I18nContext';
import { config } from '../config/runtimeConfig';
import '../styles/site-chrome.css';

/**
 * Путь причаливания в долях высоты подвала: он полностью раскрыт, когда виден
 * на 80% своей высоты. Не 100% — последние проценты добираются уже на упоре
 * прокрутки, и подвал доезжал бы рывком в самом конце.
 */
const DOCK_TRAVEL = 0.8;

const Footer = () => {
  const { t, translateInventory: isOwnStock } = useTranslation();
  const contactInfo = useContactInfo();
  const demoPanelUrl = config.IS_OWN_STOCK ? config.DEMO_PANEL_URL : null;
  // Часов работы в подвале нет намеренно (#1330): шоурума два, а блок часов
  // был один — читатель не мог понять, чьё это расписание. Часы живут там,
  // где привязаны к конкретному адресу: карточки шоурумов на главной и
  // страница «Контакты».

  /**
   * Подвал «причаливает»: пока он внизу и виден краем — это карточка с полями
   * и скруглением, как всё на странице; доехали до него — поля и нижние углы
   * сходят на нет, и он встаёт в полотно чёрным блоком. Верхние углы
   * остаются скруглёнными: подвал упирается в низ окна, а не в его верх, и
   * закруглять ему надо только ту кромку, с которой к нему подъезжают.
   *
   * Движение ПРИВЯЗАНО К ПРОКРУТКЕ, а не запускается переходом по порогу.
   * Порог с `transition` давал рывок: класс щёлкал в одной точке, и подвал
   * доигрывал свои 300ms сам по себе — независимо от того, продолжают его
   * тянуть или уже остановились. Теперь мы пишем в `--footer-dock` долю пути
   * (0…1), а вся геометрия в CSS считается из неё: подвал раскрывается ровно
   * настолько, насколько его подтянули, и замирает вместе с пальцем.
   *
   * Замер — в rAF: `scroll` приходит чаще кадра, и считать на каждом событии
   * значит делать работу, которую никто не увидит.
   */
  const footerRef = useRef(null);
  useEffect(() => {
    const el = footerRef.current;
    // Витрина (#1335): подвал всегда причален — во всю ширину, без полей и
    // нижних углов, без движения за прокруткой (`--footer-dock: 1` в
    // `site-chrome.css` под `.app-shell--own-stock`). Проверяем тот же класс,
    // а не флаг: у страницы Andys флаг есть, а класса нет, и подвал там
    // остался бы карточкой навсегда. Причаливание осталось у дилеров.
    if (!el || el.closest('.app-shell--own-stock')) return undefined;

    /**
     * Спокойное движение: подвал сразу причален.
     *
     * Проверка ЗДЕСЬ, а не только в CSS. Ветка `prefers-reduced-motion` в
     * `site-chrome.css` задаёт `--footer-dock: 1`, но `setProperty` пишет
     * ИНЛАЙН, а инлайн побеждает любое правило таблицы стилей — ветка молча
     * проигрывала, и подвал продолжал ездить за прокруткой у тех, кто
     * движение выключил.
     */
    const calm = window.matchMedia('(prefers-reduced-motion: reduce)');

    let ticking = false;
    const measure = () => {
      ticking = false;
      if (calm.matches) {
        el.style.setProperty('--footer-dock', '1');
        return;
      }
      const viewport = window.innerHeight;
      const { top, height } = el.getBoundingClientRect();
      // Путь меряется ВЫСОТОЙ САМОГО ПОДВАЛА, а не долей окна. С долей окна
      // подвал ниже неё физически не мог доехать до конца: страница
      // заканчивается, прокрутка упирается, а причаливание остаётся
      // недоделанным (замер: 0.78 на самом низу при подвале 280px и окне
      // 900px). От своей высоты он доезжает всегда, на любом экране.
      const distance = height * DOCK_TRAVEL;
      const progress = distance > 0 ? (viewport - top) / distance : 1;
      el.style.setProperty(
        '--footer-dock',
        String(Math.min(1, Math.max(0, progress)))
      );
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    calm.addEventListener('change', measure);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      calm.removeEventListener('change', measure);
    };
  }, []);

  return (
    <footer className="site-footer" ref={footerRef}>
      <div className="site-chrome__container site-footer__inner">
        <div className="site-footer__grid">
          {/* Company Info */}
          <div className="site-footer__col">
            <div className="site-footer__logo">
              <img src="/assets/logo-2lines-white.svg" alt="ShiftGears" />
            </div>
            <p className="site-footer__lead">
              {/* Своя подпись дилера — как задана; запасная подпись витрины на
                  её собственном стоке — на языке покупателя (#1431). */}
              {isOwnStock && contactInfo.company.description === DEFAULT_COMPANY_DESCRIPTION
                ? t('showcase.footer.tagline', DEFAULT_COMPANY_DESCRIPTION)
                : contactInfo.company.description}
            </p>
            {/* Копирайт стоит внизу первой колонки, а не отдельной строкой во
                всю ширину: он относится к компании, чьё имя и описание рядом.
                Отдельная центрированная строка добавляла подвалу ещё один ярус
                и пустое место под ним. */}
            <p className="site-footer__muted site-footer__copyright">
              {/* Год был захардкожен 2025 и уже врал (#1323). Имя может быть
                  пустым в widget mode — тогда строка не должна оставлять
                  висящий пробел с точкой. */}
              © {new Date().getFullYear()}
              {contactInfo.company.name
                ? ` ${contactInfo.company.name}`
                : ''}. {t('showcase.footer.rights', 'All rights reserved.')}
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="site-footer__title">
              {t('showcase.footer.quickLinks', 'Quick links')}
            </h3>
            <ul className="site-footer__list">
              <li>
                <Link to="/" className="site-footer__link">
                  {t('showcase.footer.home', 'Home')}
                </Link>
              </li>
              <li>
                <Link to="/cars" className="site-footer__link">
                  {t('showcase.footer.catalog', 'Catalog')}
                </Link>
              </li>
              <li>
                <Link to="/about" className="site-footer__link">
                  {t('showcase.footer.about', 'About us')}
                </Link>
              </li>
              {/* Названия совпадают с меню в шапке (#1330): одна и та же
                  страница под двумя именами читается как два разных раздела. */}
              <li>
                <Link to="/contact" className="site-footer__link">
                  {t('showcase.footer.chat', 'Chat with us')}
                </Link>
              </li>
              <li>
                <Link to="/showrooms" className="site-footer__link">
                  {t('showcase.footer.showroom', 'Showroom')}
                </Link>
              </li>
              {/* Демо-кабинет дилера на фикстурах (#1339) — отдельное приложение
                  путём этой витрины (/panel/), поэтому `<a>`, а не роут.
                  В новой вкладке, чтобы витрина и чат посетителя остались
                  открытыми. Без адреса в конфиге пункта нет — DEMO_PANEL_URL. */}
              {demoPanelUrl && (
                <li>
                  <a
                    href={demoPanelUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="site-footer__link"
                  >
                    {t('showcase.footer.panel', 'Dealer panel')}
                  </a>
                </li>
              )}
            </ul>
          </div>

          {/* Showroom Locations */}
          <div>
            <h3 className="site-footer__title">
              {t('showcase.footer.locations', 'Our locations')}
            </h3>
            <div className="site-footer__list site-footer__list--loose">
              {contactInfo.locations.map((location, index) => (
                <div key={index}>
                  <h4 className="site-footer__strong">{location.name}</h4>
                  <p className="site-footer__note">
                    {location.address && (
                      <>
                        {location.address}
                        <br />
                      </>
                    )}
                    {location.city && <>{location.city}</>}
                    {location.country && <>, {location.country}</>}
                  </p>
                  {location.mapsUrl && (
                    <a
                      href={location.mapsUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="site-footer__map-link"
                    >
                      {t('showcase.footer.viewOnMap', 'View on map')} →
                    </a>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="site-footer__title">
              {t('showcase.footer.contact', 'Contact info')}
            </h3>
            <div className="site-footer__list">
              <div className="site-footer__row">
                <Phone className="site-footer__icon" />
                {/* Пробелы из номера убраны: `tel:` по RFC 3966 их не
                    допускает, и часть дозвонщиков на Android отдаёт по такой
                    ссылке пустой набор. Показываем по-прежнему с пробелами. */}
                <a
                  href={`tel:${contactInfo.phone.replace(/\s/g, '')}`}
                  className="site-footer__link site-footer__link--action"
                >
                  {contactInfo.phone}
                </a>
              </div>
              <div className="site-footer__row">
                <Mail className="site-footer__icon" />
                <a
                  href={`mailto:${contactInfo.email}`}
                  className="site-footer__link site-footer__link--action"
                  onClick={(e) => {
                    window.location.href = `mailto:${contactInfo.email}`;
                  }}
                >
                  {contactInfo.email}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
